/**
 * Pure helpers for `leave_policy_groups_panel.tsx` — draft conversion,
 * validation and membership diffing. Extracted per the component-
 * testability rule (the panel is >150 lines).
 */
import type { TFunction } from '@mukil/i18n';
import type { LeaveType } from '../../data/use_leave_types';
import type {
  LeaveGroupAccrualMethod,
  LeavePolicyGroup,
  LeavePolicyGroupRule,
  UpsertLeavePolicyGroupInput,
} from '../../data/use_leave_policy_groups';

/** One leave type's override row in the group editor. */
export interface GroupRuleDraft {
  leave_type: string;
  /** Whether this group overrides the leave type's default policy. */
  enabled: boolean;
  accrual_method: LeaveGroupAccrualMethod;
  annual_entitlement_days_scaled: number | null;
  prorate_on_join: boolean;
  carry_forward_capped: boolean;
  carry_forward_max_days_scaled: number | null;
}

export interface GroupDraft {
  code: string;
  name: string;
  rules: GroupRuleDraft[];
  /** Employee ids that should be members after saving. */
  member_ids: string[];
}

/**
 * Leave types a group may override: balance-tracked, not archived and not
 * statutory (statutory stays pay-run accrued for everyone — the backend
 * rejects a statutory override).
 */
export function overridable_leave_types(
  leave_types: readonly LeaveType[],
): LeaveType[] {
  return leave_types.filter(
    (lt) =>
      lt.tracks_balance && !lt.is_archived && lt.accrual_method !== 'statutory',
  );
}

function default_rule_draft(lt: LeaveType): GroupRuleDraft {
  return {
    leave_type: lt.code,
    enabled: false,
    accrual_method:
      lt.accrual_method === 'monthly' ? 'monthly' : 'fixed_annual',
    annual_entitlement_days_scaled: lt.annual_entitlement_days_scaled,
    prorate_on_join: lt.prorate_on_join,
    carry_forward_capped: lt.carry_forward_max_days_scaled !== null,
    carry_forward_max_days_scaled: lt.carry_forward_max_days_scaled,
  };
}

/** Editor draft for a new group (every override off, seeded from defaults). */
export function make_empty_group_draft(
  leave_types: readonly LeaveType[],
): GroupDraft {
  return {
    code: '',
    name: '',
    rules: overridable_leave_types(leave_types).map(default_rule_draft),
    member_ids: [],
  };
}

/** Editor draft for an existing group. */
export function group_to_draft(
  group: LeavePolicyGroup,
  leave_types: readonly LeaveType[],
): GroupDraft {
  return {
    code: group.code,
    name: group.name,
    rules: overridable_leave_types(leave_types).map((lt) => {
      const rule = group.rules.find((r) => r.leave_type === lt.code);
      if (rule === undefined) return default_rule_draft(lt);
      return {
        leave_type: lt.code,
        enabled: true,
        accrual_method: rule.accrual_method,
        annual_entitlement_days_scaled: rule.annual_entitlement_days_scaled,
        prorate_on_join: rule.prorate_on_join,
        carry_forward_capped: rule.carry_forward_max_days_scaled !== null,
        carry_forward_max_days_scaled: rule.carry_forward_max_days_scaled,
      };
    }),
    member_ids: [...group.employee_ids],
  };
}

/** The upsert payload: only enabled rows become rules. */
export function draft_to_group_input(
  draft: GroupDraft,
): UpsertLeavePolicyGroupInput {
  const rules: LeavePolicyGroupRule[] = draft.rules
    .filter((r) => r.enabled)
    .map((r) => ({
      leave_type: r.leave_type,
      accrual_method: r.accrual_method,
      annual_entitlement_days_scaled:
        r.accrual_method === 'none' ? null : r.annual_entitlement_days_scaled,
      prorate_on_join: r.prorate_on_join,
      carry_forward_max_days_scaled: r.carry_forward_capped
        ? (r.carry_forward_max_days_scaled ?? 0)
        : null,
    }));
  return { code: draft.code, name: draft.name.trim(), rules };
}

export type GroupDraftErrors = Partial<
  Record<'code' | 'name' | 'rules', string>
>;

export function validate_group_draft(
  draft: GroupDraft,
  t: TFunction,
): GroupDraftErrors {
  const errors: GroupDraftErrors = {};
  if (!/^[a-z0-9_]{1,32}$/.test(draft.code)) {
    errors.code = t('settings.leave_groups.error_code_format');
  }
  if (!draft.name.trim()) {
    errors.name = t('settings.leave_groups.error_name_required');
  }
  const badRule = draft.rules.some(
    (r) =>
      r.enabled &&
      ((r.accrual_method !== 'none' &&
        (r.annual_entitlement_days_scaled === null ||
          r.annual_entitlement_days_scaled < 0 ||
          r.annual_entitlement_days_scaled > 36_600)) ||
        (r.carry_forward_capped &&
          (r.carry_forward_max_days_scaled === null ||
            r.carry_forward_max_days_scaled < 0 ||
            r.carry_forward_max_days_scaled > 36_600))),
  );
  if (badRule) {
    errors.rules = t('settings.leave_groups.error_rule_invalid');
  }
  return errors;
}

/**
 * The assignment calls needed to move from the current membership to the
 * draft's: employees to put in this group, and ex-members to return to the
 * default policies. Employees currently in ANOTHER group who are added here
 * simply move (one group per employee).
 */
export function membership_changes(
  group_code: string,
  current_member_ids: readonly string[],
  next_member_ids: readonly string[],
): Array<{ employee_id: string; group_code: string | null }> {
  const current = new Set(current_member_ids);
  const next = new Set(next_member_ids);
  const changes: Array<{ employee_id: string; group_code: string | null }> = [];
  for (const id of next) {
    if (!current.has(id)) changes.push({ employee_id: id, group_code });
  }
  for (const id of current) {
    if (!next.has(id)) changes.push({ employee_id: id, group_code: null });
  }
  return changes;
}

/** employee id → name of the OTHER group they belong to (for the member list). */
export function other_group_by_employee(
  groups: readonly LeavePolicyGroup[],
  editing_code: string,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const g of groups) {
    if (g.code === editing_code || g.is_archived) continue;
    for (const id of g.employee_ids) map.set(id, g.name);
  }
  return map;
}
